import React, { createContext, useContext, ReactNode } from 'react';
import { useSiteConfig } from '../hooks/useSiteConfig';

interface SiteTheme {
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };
  secondary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };
  accent: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };
}

interface SiteContextType {
  name: string;
  description: string;
  keywords: string;
  sport: string | null;
  level: string | null;
  theme: SiteTheme;
  isMainSite: boolean;
  isSportSpecific: boolean;
  isLevelSpecific: boolean;
  getApiFilters: () => Record<string, string>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const siteConfig = useSiteConfig();
  
  const isMainSite = !siteConfig.sport && !siteConfig.level;
  const isSportSpecific = !!siteConfig.sport;
  const isLevelSpecific = !!siteConfig.level;
  
  const getApiFilters = () => {
    const filters: Record<string, string> = {};
    
    if (siteConfig.sport) {
      filters.sport__slug = siteConfig.sport;
    }
    
    if (siteConfig.level) {
      filters.level__slug = siteConfig.level;
    }
    
    return filters;
  };
  
  const value: SiteContextType = {
    ...siteConfig,
    isMainSite,
    isSportSpecific,
    isLevelSpecific,
    getApiFilters,
  };
  
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};